import { isEqual } from 'lodash-es';
import { BehaviorSubject, catchError, defer, filter, Observable, take, tap, throwError } from 'rxjs';

export function createState<TState, TRequestGet = void, TExtraProps extends {} = {}>(
    optionsFn: (input: { stateSubject: BehaviorSubject<TState | undefined> }) => {
        fetch$: (request: TRequestGet) => Observable<TState>;
    } & TExtraProps
) {
    const stateSubject = new BehaviorSubject<TState | undefined>(undefined);
    const loadingSubject = new BehaviorSubject<boolean>(false);
    const options = optionsFn({ stateSubject });
    const reload$ = (request: TRequestGet) => {
        return defer(() => {
            loadingSubject.next(true);
            return options.fetch$(request).pipe(
                tap((res) => {
                    loadingSubject.next(false);
                    stateSubject.next(res);
                }),
                catchError((e) => {
                    loadingSubject.next(false);
                    return throwError(() => e);
                }),
                take(1)
            );
        });
    };
    const extraProps: TExtraProps = { ...options };
    return {
        ...extraProps,
        get$: () => {
            return stateSubject.asObservable().pipe(filter((state) => state !== undefined));
        },
        reload$,
        isLoading$: () => loadingSubject.asObservable(),
        set: (updateFn: (currentState: TState | undefined) => TState | undefined) => {
            stateSubject.next(updateFn(stateSubject.getValue()));
        },
        clear: () => {
            stateSubject.next(undefined);
        },
        stateSubject,
        loadingSubject,
    };
}

export function createLazyLoadedState<TState, TRequestGet = void, TExtraProps extends {} = {}>(
    optionsFn: (input: { stateSubject: BehaviorSubject<TState | undefined> }) => {
        fetch$: (request: TRequestGet) => Observable<TState>;
    } & TExtraProps
) {
    let prevRequestGet: TRequestGet | undefined = undefined;
    const state = createState<TState, TRequestGet, TExtraProps>(optionsFn);
    return {
        ...state,
        get$: (request: TRequestGet) => {
            const stateEmpty = state.stateSubject.getValue() === undefined && state.loadingSubject.getValue() === false;
            const inputChanged =
                typeof request !== 'object' ? request !== prevRequestGet : isEqual(prevRequestGet, request);

            prevRequestGet = request;
            if (stateEmpty || inputChanged) {
                state.reload$(request).subscribe();
            }

            return state.stateSubject.asObservable().pipe(filter((state) => state !== undefined));
        },
    };
}
